import React, { useState } from "react";
import styled from "styled-components";
import { useTheme } from "../contexts/ThemeContext";
import { postAdminRunJob } from "../services/api";

/** All five use the same Lambda (slickpicks_mlb_genai_picks) with different script payload. */
const GENAI_SCRIPT_OPTIONS = [
  { key: "genai_game_picks", label: "Game Picks only" },
  { key: "genai_picks_of_the_day", label: "Picks of the Day only" },
  { key: "genai_parlay_of_the_day", label: "Parlay of the Day only" },
  { key: "genai_both", label: "Game Picks + Picks of the Day (both)" },
  { key: "genai_all", label: "All (Game Picks + Picks + Parlay)" },
];

const OTHER_JOBS = [
  { key: "get_lineups", label: "Get Lineups", dateOptional: true },
  { key: "get_results", label: "Get Results", dateOptional: true },
  { key: "get_odds", label: "Get Odds", dateOptional: true },
  { key: "get_predictions", label: "Get Predictions", dateOptional: true },
  { key: "get_model_stats", label: "Rebuild Picks Ledger", dateOptional: false },
  { key: "append_season_archive", label: "Append Season Archive", dateOptional: true },
];

const Container = styled.div`
  max-width: 720px;
  margin: 2rem auto;
  padding: 1rem;
  color: ${(p) => p.theme.primaryText};
`;

const Title = styled.h1`
  color: ${(p) => p.theme.primaryText};
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: ${(p) => p.theme.secondaryText};
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
`;

const Card = styled.div`
  background: ${(p) => p.theme.cardBg || p.theme.secondaryBg};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
`;

const JobRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const JobLabel = styled.span`
  font-weight: 500;
  min-width: 180px;
`;

const SectionTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(p) => p.theme.secondaryText};
  margin-bottom: 0.75rem;
`;

const Select = styled.select`
  padding: 0.5rem 2rem 0.5rem 0.65rem;
  font-size: 0.95rem;
  background: ${(p) => p.theme.inputBg};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 0.35rem;
  color: ${(p) => p.theme.primaryText};
  min-width: 260px;
  cursor: pointer;
`;

const DateInput = styled.input`
  padding: 0.5rem 0.65rem;
  font-size: 0.95rem;
  background: ${(p) => p.theme.inputBg};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 0.35rem;
  color: ${(p) => p.theme.primaryText};
  width: 140px;
`;

const RunBtn = styled.button`
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  background: ${(p) => p.theme?.primary || "#4299e1"};
  color: #fff;
  border: none;
  border-radius: 0.35rem;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  &:hover:not(:disabled) {
    filter: brightness(1.1);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ResultBox = styled.div`
  margin-top: 1.25rem;
  padding: 1rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
  background: ${(p) => (p.$error ? "rgba(220, 53, 69, 0.12)" : "rgba(40, 167, 69, 0.12)")};
  border: 1px solid ${(p) => (p.$error ? "rgba(220, 53, 69, 0.4)" : "rgba(40, 167, 69, 0.4)")};
  color: ${(p) => p.theme.primaryText};
`;

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function AdminRunJobPage() {
  const theme = useTheme();
  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dateStr, setDateStr] = useState(todayISO());
  const [genaiScriptKey, setGenaiScriptKey] = useState("genai_all");

  const run = async (jobKey) => {
    setRunning(jobKey);
    setResult(null);
    setError(null);
    const payload = {};
    const isGenai = GENAI_SCRIPT_OPTIONS.some((o) => o.key === jobKey);
    const other = OTHER_JOBS.find((j) => j.key === jobKey);
    if ((isGenai || other?.dateOptional) && dateStr.trim()) payload.date = dateStr.trim();
    try {
      const data = await postAdminRunJob(jobKey, payload);
      setResult(data);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Request failed.";
      setError({ message: msg, response: err.response?.data });
    } finally {
      setRunning(null);
    }
  };

  return (
    <Container>
      <Title>Run pipeline job</Title>
      <Subtitle>
        Invoke a pipeline Lambda from the server. Only available when the backend has{" "}
        <code>ENABLE_LAMBDA_RUN_JOBS=1</code> and AWS credentials with <code>lambda:InvokeFunction</code>.
      </Subtitle>

      <Card theme={theme}>
        <div style={{ marginBottom: "0.75rem", color: theme?.secondaryText, fontSize: "0.9rem" }}>
          Optional date (YYYY-MM-DD) for jobs that accept it:
        </div>
        <DateInput
          type="date"
          value={dateStr}
          onChange={(e) => setDateStr(e.target.value)}
          theme={theme}
        />
      </Card>

      <Card theme={theme}>
        <SectionTitle theme={theme}>Other pipeline jobs</SectionTitle>
        {OTHER_JOBS.map((j) => (
          <JobRow key={j.key}>
            <JobLabel theme={theme}>{j.label}</JobLabel>
            <RunBtn
              theme={theme}
              onClick={() => run(j.key)}
              disabled={running !== null}
              aria-label={`Run ${j.label}`}
            >
              {running === j.key ? "Running…" : "Run"}
            </RunBtn>
          </JobRow>
        ))}
      </Card>

      <Card theme={theme}>
        <SectionTitle theme={theme}>GenAI Picks (one Lambda, choose script)</SectionTitle>
        <JobRow>
          <Select
            theme={theme}
            value={genaiScriptKey}
            onChange={(e) => setGenaiScriptKey(e.target.value)}
            aria-label="GenAI script"
          >
            {GENAI_SCRIPT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </Select>
          <RunBtn
            theme={theme}
            onClick={() => run(genaiScriptKey)}
            disabled={running !== null}
            aria-label={`Run ${GENAI_SCRIPT_OPTIONS.find((o) => o.key === genaiScriptKey)?.label}`}
          >
            {running === genaiScriptKey ? "Running…" : "Run"}
          </RunBtn>
        </JobRow>
      </Card>

      {result && (
        <ResultBox theme={theme} $error={false}>
          <strong>Success</strong>
          {"\n"}
          {JSON.stringify(result, null, 2)}
        </ResultBox>
      )}
      {error && (
        <ResultBox theme={theme} $error>
          <strong>Error</strong>
          {"\n"}
          {error.message}
          {error.response && Object.keys(error.response).length > 1 && (
            <>
              {"\n"}
              {JSON.stringify(error.response, null, 2)}
            </>
          )}
        </ResultBox>
      )}
    </Container>
  );
}
